import { useState } from "react";
import { imageSrcSet } from "@majourneys/shared";
import { cn } from "../../lib/utils";

type ImageWithFallbackProps = {
  src: string | null | undefined;
  alt: string;
  className?: string;
  loading?: "eager" | "lazy";
  fetchPriority?: "high" | "low" | "auto";
  decoding?: "async" | "sync" | "auto";
  width?: number;
  height?: number;
  /**
   * Rendered width hint (CSS pixels) at common breakpoints, i.e. the `sizes`
   * attribute. Only emitted when the host can be resized (Cloudflare Images or
   * cdn.majourneys.com); other hosts get the plain `src` and nothing else.
   */
  sizes?: string;
  /** Pixel widths to generate in the srcset. Defaults to a sensible range. */
  srcsetWidths?: readonly number[];
  /** Fires once the photo (resized or plain) has loaded. */
  onLoad?: () => void;
  /** Called once the original has failed too, i.e. when the grey block shows. */
  onFail?: () => void;
  ref?: React.Ref<HTMLImageElement>;
  /**
   * An inline script may add attributes before hydration (BlurUpImage's
   * `data-loaded`); this keeps React from warning about them.
   */
  suppressHydrationWarning?: boolean;
};

const DEFAULT_SRCSET_WIDTHS = [240, 360, 480, 720, 1080, 1440] as const;
const DEFAULT_SIZES = "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 240px";

/**
 * "resized": try the sized candidates. "plain": the resizer failed for this
 * file, ask for the original. "failed": the original failed too; grey block.
 */
type Stage = "resized" | "plain" | "failed";

/** Renders a neutral block when the URL is missing, so we never pass an empty `src`. */
export function ImageWithFallback({
  src,
  alt,
  className,
  loading = "lazy",
  fetchPriority = "auto",
  decoding = "async",
  width,
  height,
  sizes,
  srcsetWidths = DEFAULT_SRCSET_WIDTHS,
  onLoad,
  onFail,
  ref,
  suppressHydrationWarning,
}: ImageWithFallbackProps) {
  const url = typeof src === "string" ? src.trim() : "";
  // Keyed by url so a card that is reused for a different image starts over.
  const [state, setState] = useState<{ url: string; stage: Stage }>({ url, stage: "resized" });
  const stage: Stage = state.url === url ? state.stage : "resized";

  if (!url || stage === "failed") {
    return <div className={cn("bg-[#ececec]", className)} aria-hidden />;
  }

  const srcSet = stage === "resized" ? imageSrcSet(url, srcsetWidths) : undefined;
  return (
    <img
      src={url}
      srcSet={srcSet}
      sizes={srcSet ? sizes ?? DEFAULT_SIZES : undefined}
      alt={alt}
      className={className}
      loading={loading}
      fetchPriority={fetchPriority}
      decoding={decoding}
      width={width}
      height={height}
      ref={ref}
      suppressHydrationWarning={suppressHydrationWarning}
      onLoad={onLoad}
      onError={() => {
        setState({ url, stage: srcSet ? "plain" : "failed" });
        if (!srcSet) onFail?.();
      }}
    />
  );
}
